import { DEFAULT_CONSTANTS, type GridState, type SimConstants } from "../sim/types.ts";
import { busInjections, solveDCPF } from "../sim/network.ts";
import { demandFor, stepFrequency, stepGenerator, totalDemand, totalSupply } from "../sim/physics.ts";

export interface GameContext {
  state: GridState;
  constants: SimConstants;
  winSeconds: number;       // total game-seconds to stay in band (or other win metric)
  budgetUSD?: number;       // optional cost cap (layer 2+)
  events: GameEvent[];
  // Layer-specific win/lose logic override (overrides default frequency-band logic).
  // Return "won", "lost <reason>", or "" to continue.
  customOutcome?: (ctx: GameContext, dtMin: number) => "" | { status: "won" | "lost"; reason?: string };
  // Layer 8 restoration target (0..1 of restorable demand)
  restorationTarget?: number;
  // Layer 5 contingency: islanded-load tolerance in game-minutes
  blackoutToleranceMin?: number;
  // Internal accumulator
  blackoutTimer?: number;
}

export type GameEvent = {
  t: number; // game-minutes from start at which to fire
  fired?: boolean;
  apply: (ctx: GameContext) => void;
  label: string;
};

export function emptyGrid(): GridState {
  return {
    gameMinutes: 0,
    frequency: DEFAULT_CONSTANTS.nominalFrequency,
    supplyMW: 0,
    demandMW: 0,
    generators: [],
    loads: [],
    cumulativeCost: 0,
    status: "playing",
    winProgressSeconds: 0,
  };
}

const OVERLOAD_TRIP_MIN = 3; // game-minutes of sustained overload before trip
const ISLAND_BLACKOUT_MIN_DEFAULT = 8;

/** Advance the entire game by dt game-minutes. */
export function step(ctx: GameContext, dtMin: number): void {
  const { state, constants } = ctx;
  if (state.status !== "playing") return;

  state.gameMinutes += dtMin;

  // Fire scheduled events
  for (const ev of ctx.events) {
    if (!ev.fired && state.gameMinutes >= ev.t) {
      ev.apply(ctx);
      ev.fired = true;
    }
  }

  // Generators
  for (const g of state.generators) stepGenerator(g, dtMin, state.gameMinutes);

  // Network: solve DC PF, check overloads, handle islanding
  if (state.network) {
    const inj = busInjections(state.network, state.generators, state.loads, (id) => demandFor(state.loads, state.gameMinutes, id));
    const pf = solveDCPF(state.network, inj);
    state.islandedBuses = [...pf.islanded];

    // Overload trip logic
    for (const line of state.network.lines) {
      if (line.status === "tripped") continue;
      if (Math.abs(line.flow) > line.thermalLimit) {
        line.overloadTimer += dtMin;
        if (line.overloadTimer >= OVERLOAD_TRIP_MIN) {
          line.status = "tripped";
          line.trippedAt = state.gameMinutes;
        }
      } else {
        line.overloadTimer = Math.max(0, line.overloadTimer - dtMin * 0.5);
      }
    }

    // Loads on islanded buses are effectively shed (not counted in demand)
    for (const l of state.loads) {
      const bus = state.network.loadBus.get(l.id);
      if (bus && pf.islanded.has(bus)) {
        // Note: keep load alive (shed=false) so restoration can happen; just deduct from demand
      }
    }
  }

  // Demand from loads (excluding shed + islanded)
  let demand = 0;
  for (const l of state.loads) {
    if (l.shed) continue;
    if (state.network) {
      const bus = state.network.loadBus.get(l.id);
      if (bus && state.islandedBuses && state.islandedBuses.includes(bus)) continue;
    }
    const mult = l.curve ? l.curve(state.gameMinutes) : 1;
    demand += l.baseDemand * mult;
  }
  state.demandMW = demand;

  // Supply = sum of generators (only those connected to slack count toward freq)
  let supply = 0;
  for (const g of state.generators) {
    if (state.network) {
      const bus = state.network.genBus.get(g.id);
      if (bus && state.islandedBuses && state.islandedBuses.includes(bus)) continue;
    }
    supply += g.output;
  }
  state.supplyMW = supply;

  // Frequency dynamics
  stepFrequency(state, constants, dtMin);

  // Cost accrual: $/MWh * MW * hours (only for positive output)
  const dtHours = dtMin / 60;
  for (const g of state.generators) {
    if (g.status !== "on") continue;
    const energy = Math.max(0, g.output) * dtHours;
    state.cumulativeCost += g.cost * energy;
  }

  // Default outcome logic (can be overridden by customOutcome)
  if (ctx.customOutcome) {
    const out = ctx.customOutcome(ctx, dtMin);
    if (out !== "") {
      state.status = out.status;
      if (out.status === "lost") state.loseReason = out.reason;
      return;
    }
  }

  // Frequency band tracking
  const inBand =
    state.frequency >= constants.bandLow && state.frequency <= constants.bandHigh;
  if (inBand) {
    state.winProgressSeconds += dtMin;
  } else {
    state.winProgressSeconds = Math.max(0, state.winProgressSeconds - dtMin * 0.5);
  }

  // Islanded-load tolerance (layer 4+)
  if (state.network) {
    const hasIslandedLoad = state.loads.some((l) => {
      const bus = state.network!.loadBus.get(l.id);
      return bus && state.islandedBuses?.includes(bus) && !l.shed;
    });
    const tol = ctx.blackoutToleranceMin ?? ISLAND_BLACKOUT_MIN_DEFAULT;
    if (hasIslandedLoad) {
      ctx.blackoutTimer = (ctx.blackoutTimer ?? 0) + dtMin;
      if (ctx.blackoutTimer > tol) {
        state.status = "lost";
        state.loseReason = `Customers were without power for ${tol.toFixed(0)}+ game-minutes.`;
        return;
      }
    } else {
      ctx.blackoutTimer = Math.max(0, (ctx.blackoutTimer ?? 0) - dtMin);
    }
  }

  if (state.frequency <= constants.tripLow) {
    state.status = "lost";
    state.loseReason = `Under-frequency trip at ${state.frequency.toFixed(2)} Hz — load shedding cascade.`;
  } else if (state.frequency >= constants.tripHigh) {
    state.status = "lost";
    state.loseReason = `Over-frequency trip at ${state.frequency.toFixed(2)} Hz — generators disconnected.`;
  } else if (ctx.budgetUSD !== undefined && state.cumulativeCost > ctx.budgetUSD) {
    state.status = "lost";
    state.loseReason = `Budget exceeded: $${state.cumulativeCost.toFixed(0)} > $${ctx.budgetUSD.toFixed(0)}.`;
  } else if (state.winProgressSeconds >= ctx.winSeconds) {
    state.status = "won";
  }
}

// Re-export for legacy users
export { totalDemand, totalSupply };
